import React, {useEffect, useMemo, useState} from 'react'
import Header from '../components/Header'
import PomodoroTimer from "./PomodoroTimer";
import {useAuth} from "../authentication/AuthContext";
import StopwatchTimer from "./StopwatchTimer";
import {useTask} from "../task/hooks/useTasks";
import {useProjects} from "../project/hooks/useProjects";
import {useLocation} from 'react-router-dom';
import {useQueryClient} from "react-query";
import {createFolder, fetchFolders} from "../folder/api/folderApi";
import {filterFoldersByScope, getNextFolderSortOrder, sortFolders} from "../folder/utils/folders";
import {useCreateNote, useUpdateNote} from "../note/hooks/useNote";
import {NOTE_SCOPE, SESSIONS_FOLDER_NAME, WORK_SESSION_NOTE_SOURCE} from "../note/noteConstants";
import {createPlainTextEditorState} from "../note/noteEditorDefaults";
import {fetchNotes} from "../note/api/notesApi";
import {fetchSettings} from "../settings/api/settingsApi";
import {DEFAULT_BREAK_DURATION_MINUTES, readBreakDurationMinutes} from "../settings/settingsPreferences";
import {TIMER_TYPE_STORAGE_KEY} from "./timerStorage";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import "prismjs/components/prism-java";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-go";
import "prismjs/components/prism-kotlin";
import "prismjs/themes/prism-tomorrow.css";

const DEFAULT_POMODORO_MINUTES = 1;
const FENCED_CODE_BLOCK_REGEX = /```([a-zA-Z0-9_-]+)?[ \t]*\n([\s\S]*?)```/g;

function getDateKey(dateValue = new Date()) {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function buildSessionNoteTitle() {
    return `Session Notes - ${new Date().toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}`;
}

function extractPlainTextFromNode(node) {
    if (!node) return '';
    if (node.type === 'linebreak') return '\n';
    if (node.type === 'text') return node.text ?? '';
    if (!Array.isArray(node.children)) return '';

    return node.children.map(extractPlainTextFromNode).join('');
}

function extractPlainTextFromEditorState(raw) {
    if (!raw) return '';

    try {
        const json = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const root = json?.root ?? json;
        if (!Array.isArray(root?.children)) {
            return extractPlainTextFromNode(root).trim();
        }

        return root.children
            .map((child) => extractPlainTextFromNode(child).trim())
            .join('\n')
            .trim();
    } catch {
        return typeof raw === 'string' ? raw.trim() : '';
    }
}

function findTodaysReusableSessionNote(notes, folderId) {
    const todayKey = getDateKey(new Date());

    return notes
        .filter((note) => note?.noteSource === WORK_SESSION_NOTE_SOURCE && note?.parentFolderId === folderId)
        .filter((note) => {
            const referenceDate = note?.updatedAt ?? note?.createdAt ?? null;
            return getDateKey(referenceDate) === todayKey;
        })
        .sort((left, right) => {
            const leftTime = new Date(left?.updatedAt ?? left?.createdAt ?? 0).getTime();
            const rightTime = new Date(right?.updatedAt ?? right?.createdAt ?? 0).getTime();
            return rightTime - leftTime;
        })[0] ?? null;
}

function buildSessionEntryText({text, projectName, taskName}) {
    const noteText = text.trim();
    if (!noteText) return '';

    const contextParts = [projectName, taskName].filter(Boolean);
    const contextLabel = contextParts.length > 0 ? ` · ${contextParts.join(' / ')}` : '';
    const timeLabel = new Date().toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
    return `[${timeLabel}]${contextLabel}\n${noteText}`;
}

function normalizeFenceLanguage(language) {
    const normalized = String(language ?? '').trim().toLowerCase();

    switch (normalized) {
        case 'js':
            return 'javascript';
        case 'py':
            return 'python';
        case 'golang':
            return 'go';
        case 'kt':
            return 'kotlin';
        case 'txt':
        case 'text':
        case 'plain':
            return 'plain';
        default:
            return normalized || 'plain';
    }
}

function highlightSessionText(rawCode) {
    if (!rawCode) return '';

    let html = '';
    let cursor = 0;
    FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
    let match = FENCED_CODE_BLOCK_REGEX.exec(rawCode);

    while (match) {
        const [fullMatch, language, codeBlock] = match;
        const matchStart = match.index;
        const beforeBlock = rawCode.slice(cursor, matchStart);

        html += Prism.util.encode(beforeBlock);
        html += `<span class="token punctuation">${Prism.util.encode(`\`\`\`${language ?? ''}\n`)}</span>`;

        const normalizedLanguage = normalizeFenceLanguage(language);
        if (normalizedLanguage !== 'plain' && Prism.languages[normalizedLanguage]) {
            html += Prism.highlight(codeBlock, Prism.languages[normalizedLanguage], normalizedLanguage);
        } else {
            html += Prism.util.encode(codeBlock);
        }

        html += `<span class="token punctuation">\`\`\`</span>`;

        cursor = matchStart + fullMatch.length;
        match = FENCED_CODE_BLOCK_REGEX.exec(rawCode);
    }

    html += Prism.util.encode(rawCode.slice(cursor));
    return html;
}

function WorkSession() {
    const {authToken} = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [timerType, setTimerType] = useState(() => {
        try {
            const persistedValue = localStorage.getItem(TIMER_TYPE_STORAGE_KEY);
            return persistedValue === 'stopwatch' ? 'stopwatch' : 'pomodoro';
        } catch (error) {
            console.warn('Failed to read persisted timer mode:', error);
            return 'pomodoro';
        }
    });
    const [pomodoroMinutes, setPomodoroMinutes] = useState(DEFAULT_POMODORO_MINUTES);
    const [breakMinutes, setBreakMinutes] = useState(DEFAULT_BREAK_DURATION_MINUTES);
    const [breakTasks, setBreakTasks] = useState([]);
    const [isPomodoroSettingsLoading, setIsPomodoroSettingsLoading] = useState(true);
    const {data: tasks} = useTask(authToken)
    const {data: projects} = useProjects(authToken)
    const [selectedProject, setSelectedProject] = useState('');
    const [selectedTask, setSelectedTask] = useState('');
    const [isSessionCompleted, setIsSessionCompleted] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [currentSessionNoteId, setCurrentSessionNoteId] = useState(null);
    const [sessionNotes, setSessionNotes] = useState('');
    const [saveError, setSaveError] = useState('');
    const [savedAt, setSavedAt] = useState('');
    const [shouldCreateNewNote, setShouldCreateNewNote] = useState(false);
    const {mutateAsync: createNote, isLoading: isCreatingNote} = useCreateNote(authToken);
    const {mutateAsync: updateNote, isLoading: isUpdatingNote} = useUpdateNote(authToken);

    useEffect(() => {
        const projectIdFromState = location.state?.projectId;
        if (projectIdFromState != null) {
            setSelectedProject(String(projectIdFromState));
        }
        const taskIdFromState = location.state?.taskId;
        if (taskIdFromState != null) {
            setSelectedTask(String(taskIdFromState));
        }
    }, [location.state]);

    useEffect(() => {
        try {
            localStorage.setItem(TIMER_TYPE_STORAGE_KEY, timerType);
        } catch (error) {
            console.warn('Failed to persist timer mode:', error);
        }
    }, [timerType]);

    useEffect(() => {
        let isMounted = true;

        async function loadPomodoroSettings() {
            if (!authToken) {
                if (isMounted) {
                    setIsPomodoroSettingsLoading(false);
                }
                return;
            }

            try {
                const settings = await fetchSettings(authToken);
                const nextMinutes = Number(settings?.pomodoro_cycle_duration ?? settings?.pomodoroCycleDuration ?? DEFAULT_POMODORO_MINUTES);
                const nextBreakMinutes = readBreakDurationMinutes(settings);
                const nextBreakTasks = Array.isArray(settings?.break_tasks ?? settings?.breakTasks)
                    ? (settings?.break_tasks ?? settings?.breakTasks).filter((t) => typeof t === 'string')
                    : [];
                if (isMounted) {
                    setPomodoroMinutes(Number.isFinite(nextMinutes) && nextMinutes > 0 ? Math.trunc(nextMinutes) : DEFAULT_POMODORO_MINUTES);
                    setBreakMinutes(nextBreakMinutes);
                    setBreakTasks(nextBreakTasks);
                }
            } catch (error) {
                if (isMounted) {
                    setPomodoroMinutes(DEFAULT_POMODORO_MINUTES);
                    setBreakMinutes(DEFAULT_BREAK_DURATION_MINUTES);
                }
            } finally {
                if (isMounted) {
                    setIsPomodoroSettingsLoading(false);
                }
            }
        }

        loadPomodoroSettings();

        return () => {
            isMounted = false;
        };
    }, [authToken]);

    const filteredTasks = useMemo(() => (
        selectedProject
            ? (tasks || []).filter((task) => String(task?.projectId ?? '') === selectedProject)
            : (tasks || [])
    ), [selectedProject, tasks]);

    const time = useMemo(() => {
        const nextTime = new Date();
        nextTime.setSeconds(nextTime.getSeconds() + (pomodoroMinutes * 60));
        return nextTime;
    }, [pomodoroMinutes]);
    const selectedProjectName = useMemo(
        () => projects?.find((project) => String(project.id ?? project.projectId ?? '') === selectedProject)?.name ?? '',
        [projects, selectedProject]
    );
    const selectedTaskName = useMemo(
        () => tasks?.find((task) => String(task.id ?? '') === selectedTask)?.text ?? '',
        [tasks, selectedTask]
    );
    const isSavingNotes = isCreatingNote || isUpdatingNote;

    useEffect(() => {
        if (!selectedTask) return;
        const selectedStillExists = filteredTasks.some((task) => String(task.id) === selectedTask);
        if (!selectedStillExists) {
            setSelectedTask('');
        }
    }, [filteredTasks, selectedTask]);

    const handleProjectChange = (event) => {
        setSelectedProject(event.target.value);
        setIsSessionCompleted(false);
        setCurrentSessionId(null);
        setCurrentSessionNoteId(null);
        setSaveError('');
        setSavedAt('');
    }

    const handleTaskChange = (event) => {
        setSelectedTask(event.target.value);
        setIsSessionCompleted(false);
        setCurrentSessionId(null);
        setCurrentSessionNoteId(null);
        setSaveError('');
        setSavedAt('');
    }

    const handleSessionStart = (sessionId) => {
        setCurrentSessionId(sessionId ?? null);
        setSessionNotes('');
        setIsSessionCompleted(false);
        setSaveError('');
        setSavedAt('');
    };

    const handleSessionComplete = (sessionId) => {
        setCurrentSessionId(sessionId ?? null);
        setIsSessionCompleted(true);
        setSaveError('');
        setSavedAt('');
    };

    const handleNotesChange = (event) => {
        setSessionNotes(event.target.value);
        setSaveError('');
        setSavedAt('');
    };

    const handleCreateNewNoteRequest = () => {
        setShouldCreateNewNote(true);
        setCurrentSessionNoteId(null);
        setSessionNotes('');
        setSaveError('');
        setSavedAt('');
    };

    const ensureSessionNotesFolder = async () => {
        const folders = await queryClient.fetchQuery(['folders', NOTE_SCOPE], async () => (
            filterFoldersByScope(NOTE_SCOPE, await fetchFolders(authToken))
        ));
        const existingFolder = folders.find((folder) => (
            (folder.parentFolderId ?? null) === null && folder.name === SESSIONS_FOLDER_NAME
        ));

        if (existingFolder) {
            return existingFolder;
        }

        const createdFolder = await createFolder(authToken, {
            name: SESSIONS_FOLDER_NAME,
            parentFolderId: null,
            sortOrder: getNextFolderSortOrder(folders, null),
            scope: NOTE_SCOPE,
        });

        queryClient.setQueryData(['folders', NOTE_SCOPE], (currentFolders = []) => {
            const mergedFolders = [...currentFolders.filter((folder) => folder.id !== createdFolder.id), createdFolder];
            return sortFolders(mergedFolders);
        });

        return createdFolder;
    };

    const handleSaveNotes = async () => {
        setSaveError('');

        try {
            const sessionNotesFolder = await ensureSessionNotesFolder();
            const allNotes = await queryClient.fetchQuery(['notes'], () => fetchNotes(authToken));
            const freshEntry = buildSessionEntryText({
                text: sessionNotes,
                projectName: selectedProjectName,
                taskName: selectedTaskName,
            });

            if (!freshEntry) {
                throw new Error('Cannot save empty session notes');
            }

            const currentlySelectedNote = allNotes.find((note) => note?.id === currentSessionNoteId);
            const isCurrentNoteReusable = currentlySelectedNote
                && currentlySelectedNote.noteSource === WORK_SESSION_NOTE_SOURCE
                && currentlySelectedNote.parentFolderId === sessionNotesFolder.id
                && getDateKey(currentlySelectedNote.updatedAt ?? currentlySelectedNote.createdAt) === getDateKey(new Date());
            const reusableNote = shouldCreateNewNote
                ? null
                : (isCurrentNoteReusable ? currentlySelectedNote : findTodaysReusableSessionNote(allNotes, sessionNotesFolder.id));
            const previousText = reusableNote
                ? (reusableNote.plainText?.trim() || extractPlainTextFromEditorState(reusableNote.content))
                : '';
            const mergedText = previousText ? `${previousText}\n\n${freshEntry}` : freshEntry;
            const noteData = {
                content: createPlainTextEditorState(mergedText),
                parentFolderId: sessionNotesFolder.id,
                sessionId: reusableNote?.sessionId ?? currentSessionId,
                noteSource: WORK_SESSION_NOTE_SOURCE,
                projectId: reusableNote?.projectId ?? (selectedProject || null),
                title: reusableNote?.title || buildSessionNoteTitle(),
            };

            if (reusableNote?.id) {
                await updateNote({
                    id: reusableNote.id,
                    noteData,
                });
                setCurrentSessionNoteId(reusableNote.id);
            } else {
                const createdNote = await createNote(noteData);
                setCurrentSessionNoteId(createdNote?.id ?? null);
            }

            setShouldCreateNewNote(false);
            setSavedAt(new Date().toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'}));
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save session notes');
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)]">
            <Header/>
            <div className="mt-24 px-6 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {/* Left settings panel (1/3) */}
                <div className="space-y-8 md:col-span-1">
                    {/* Project select */}
                    <div className="space-y-2">
                        <label htmlFor="project" className="text-sm font-medium text-[var(--text-secondary)]">Select Project</label>
                        <div className="relative">
                            <select
                                id="project"
                                value={selectedProject}
                                onChange={handleProjectChange}
                                className="appearance-none w-full bg-[var(--surface-deep)] text-[var(--text-primary)] border border-[var(--border-strong)] rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)] focus:border-transparent cursor-pointer hover:bg-[var(--surface-hover)] transition-colors text-sm"
                            >
                                <option value="">Choose a project...</option>
                                {projects?.map((project) => {
                                    const projectValue = String(project.id ?? project.projectId ?? '');
                                    return (
                                        <option key={projectValue || project.name} value={projectValue}>{project.name}</option>
                                    );
                                })}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M19 9l-7 7-7-7"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                    {/* Task select */}
                    <div className="space-y-2">
                        <label htmlFor="task" className="text-sm font-medium text-[var(--text-secondary)]">Select Task</label>
                        <div className="relative">
                            <select
                                id="task"
                                value={selectedTask}
                                onChange={handleTaskChange}
                                className="appearance-none w-full bg-[var(--surface-deep)] text-[var(--text-primary)] border border-[var(--border-strong)] rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-[var(--brand-coral)] focus:border-transparent cursor-pointer hover:bg-[var(--surface-hover)] transition-colors text-sm"
                            >
                                <option value="">Choose a task...</option>
                                {filteredTasks.map((todo) => (
                                    <option key={todo.id} value={String(todo.id)}>{todo.text}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                          d="M19 9l-7 7-7-7"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Timer type toggle */}
                    <div className="space-y-3">
                      <span className="text-sm font-medium text-[var(--text-secondary)]">
                        Timer Mode
                      </span>
                        <div className="flex items-center gap-3">
                            <span
                                className={`text-xs font-medium ${timerType === 'pomodoro' ? 'text-[var(--brand-coral)]' : 'text-[var(--text-secondary)]'}`}>Pomodoro</span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={timerType === 'stopwatch'}
                                aria-label="Switch timer mode"
                                onClick={() => setTimerType(timerType === 'pomodoro' ? 'stopwatch' : 'pomodoro')}
                                className="relative h-6 w-12 rounded-full border border-[var(--border-strong)] bg-[var(--surface-hover)] transition-colors hover:border-[var(--button-primary-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--button-primary-bg)] focus:ring-offset-2 focus:ring-offset-[var(--page-bg)]"
                            >
                                <div
                                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-[var(--button-primary-bg)] shadow-sm transition-all duration-200 ${
                                        timerType === 'pomodoro' ? 'left-0.5' : 'left-6'
                                    }`}
                                />
                            </button>
                            <span
                                className={`text-xs font-medium ${timerType === 'stopwatch' ? 'text-[var(--brand-coral)]' : 'text-[var(--text-secondary)]'}`}>Stopwatch</span>
                        </div>
                    </div>
                </div>

                {/* Right main content (2/3) */}
                <div className="md:col-span-2 space-y-10">
                    <div className="flex justify-center">
                        {timerType === 'pomodoro' && (
                            <PomodoroTimer
                                expiryTimestamp={time}
                                defaultDurationSeconds={pomodoroMinutes * 60}
                                breakDurationSeconds={breakMinutes * 60}
                                isSettingsLoading={isPomodoroSettingsLoading}
                                project={selectedProject}
                                task={selectedTask}
                                authToken={authToken}
                                onSessionStart={handleSessionStart}
                                onSessionComplete={handleSessionComplete}
                                breakTasks={breakTasks}
                            />
                        )}
                        {timerType === 'stopwatch' && (
                            <StopwatchTimer
                                project={selectedProject}
                                task={selectedTask}
                                authToken={authToken}
                                onSessionStart={handleSessionStart}
                                onSessionComplete={handleSessionComplete}
                            />
                        )}
                    </div>

                    <div className="flex justify-center">
                        <div className="w-full max-w-3xl">
                            <label className="mb-2 block text-sm font-medium text-[var(--text-secondary)]" htmlFor="session-notes">
                                Session notes
                            </label>
                            <div className="overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] focus-within:border-[var(--brand-coral)]/50">
                                <Editor
                                    textareaId="session-notes"
                                    value={sessionNotes}
                                    onValueChange={(value) => handleNotesChange({target: {value}})}
                                    highlight={(code) => highlightSessionText(code)}
                                    padding={14}
                                    spellCheck={false}
                                    style={{
                                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                        fontSize: 13,
                                        minHeight: "240px",
                                        lineHeight: "1.6",
                                        caretColor: "var(--text-primary)",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleSaveNotes}
                                disabled={!isSessionCompleted || !currentSessionId || !sessionNotes.trim() || isSavingNotes}
                                className="inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95 disabled:cursor-not-allowed disabled:border-[var(--border-color)] disabled:bg-[var(--surface-deep)] disabled:text-[var(--text-muted)] disabled:brightness-100"
                            >
                                {isSavingNotes ? 'Saving...' : 'Save to Notes'}
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateNewNoteRequest}
                                disabled={isSavingNotes}
                                className="inline-flex items-center gap-2 rounded-md bg-transparent hover:bg-[var(--surface-hover)] active:bg-[var(--surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-[var(--text-secondary)] border border-[var(--border-color)] transition"
                            >
                                Create new note
                            </button>
                        </div>
                        <span className={`text-xs ${saveError ? 'text-red-400' : 'text-[var(--text-muted)]'}`}>
                            {saveError
                                ? saveError
                                : shouldCreateNewNote
                                    ? 'Next save will create a new note explicitly.'
                                    : savedAt
                                        ? `Saved at ${savedAt}`
                                        : 'Saves into today\'s session note. Use "Create new note" to start a separate one.'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WorkSession
